import React, { useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import { faTrashAlt } from "@fortawesome/pro-solid-svg-icons";
import API from "Signups/API";
import { getSignupErrorMessage } from "Signups/SharedComponents/getSignupErrorMessage";
import { signupItemStateHooks } from "Signups/SignupView/SignupSheet/Screens/SignupItems/stateHooks";
import { useGetSignupListType } from "Signups/SignupView/utils/useGetSignupListType";
import { useSignups } from "Signups/state";
import { motion } from "framer-motion";

import FnrButton from "UI/FnrButton3";
import FnrConfirmationPopover from "UI/FnrConfirmationPopover";

import useHovering from "Utilities/Hooks/useHovering";

import DeletePaidSignupModal from "../../DeletePaidSignupModal";
import { personType } from "../../DeletePaidSignupModal/personType";

import AddRespondent from "./AddRespondent";
import AdditionalName from "./AdditionalName";
import EditNumberOfSlotsButton from "./EditNumberOfSlotsButton";
import ExtraSlotsWithoutNames from "./ExtraSlotsWithoutNames";
import HoverButton from "./HoverButton";
import NameWithProfileLink from "./NameWithProfileLink";
import DonationPill from "./SignupPayments/DonationPill";
import PaymentPill from "./SignupPayments/PaymentPill";
import UserSignupHistoryButton from "./UserSignupHistoryButton";

export default function Respondent({
  highlight,
  user,
  signupItemID,
  hasSignupCost,
  setRemovalType,
  removalType,
}) {
  const {
    userID,
    name,
    numberOfSlots,
    additionalNames,
    userSignupHistory,
    totalAmountPaid,
  } = user;
  const [state, dispatch] = useSignups();
  const [isHovering, hoverProps] = useHovering();
  const [status, setStatus] = useState(null);
  const confirmationRef = useRef();
  const { isMemberType, isItemType } = useGetSignupListType();
  const { getSignupItem } = signupItemStateHooks.useGetSignupItem();

  const [showDeletePopover, setShowDeletePopover] = useState(false);
  const [showHistoryPopover, setShowHistoryPopover] = useState(false);
  const [showNumSlotsPopover, setShowNumSlotsPopover] = useState(false);
  const [showAddPopover, setShowAddPopover] = useState(null); // null, "household", "additionalName"
  const [hasActiveHoverItem, setHasActiveHoverItem] = useState(null);
  const [showDeletePaidSignupModal, setShowDeletePaidSignupModal] =
    useState(false);
  const [updatedNumberOfSlots, setUpdatedNumberOfSlots] = useState();

  const isSuggestedDonation =
    state.currentSignupList?.fundIt?.isSuggestedDonation;

  // This is used to keep the hover buttons visible if any items popover is currently open.
  useEffect(() => {
    if (
      showDeletePopover ||
      showHistoryPopover ||
      showNumSlotsPopover ||
      showAddPopover
    ) {
      setHasActiveHoverItem(true);
    } else {
      setHasActiveHoverItem(false);
    }
  }, [
    showDeletePopover,
    showHistoryPopover,
    showNumSlotsPopover,
    showAddPopover,
  ]);

  const handleTrashCanClick = () => {
    // const isPaidSignup = paymentStatus === "Paid";

    if (hasSignupCost) {
      setRemovalType(personType.USER);
      setShowDeletePaidSignupModal(true);
      return;
    }

    toggleDeletePopover();
  };

  const toggleDeletePopover = () => setShowDeletePopover(!showDeletePopover);

  const handleDeleteClick = () => {
    setStatus("loading");
    API.cancelOtherUserSignup({
      signupListID: state.currentSignupList.ID,
      signupItemID,
      userID,
      signedUpViaID: 5,
    })
      .post()
      .then((resp) => {
        setStatus(null);
        dispatch({
          type: "Other User Signup Updated",
          signupList: resp.signupList,
          allSignupLists: resp.allSignupLists,
        });
        getSignupItem(signupItemID);
        setShowDeletePopover(false);
      })
      .catch((err) => {
        alert(getSignupErrorMessage(err?.error));
        setStatus(null);
        setShowDeletePopover(false);
      });
  };

  const renderNumberOfItemSlots = () => {
    if (!isItemType) return null;
    return ` - ${numberOfSlots}`;
  };

  const unpaidSlotsCount = () => {
    if (!hasSignupCost) return;

    let count = 0;
    if (user?.paymentStatus === "Unpaid") {
      count++;
    }

    user?.additionalNames?.forEach((name) => {
      if (name?.paymentStatus === "Unpaid") {
        count++;
      }
    });
    return count;
  };

  const paidSlotsCount = () => {
    if (!hasSignupCost) return;

    let count = 0;
    if (user?.paymentStatus === "Paid") {
      count++;
    }

    user?.additionalNames?.forEach((name) => {
      if (name?.paymentStatus === "Paid") {
        count++;
      }
    });
    return count;
  };

  const pillProps = {
    name: name,
    additionalNameID: user?.additionalNameID,
    unpaidSlotsCount: unpaidSlotsCount(),
    isItemType: isItemType,
    style: { marginLeft: "8px" },
    amount: user?.paymentAmount,
    date: user?.paymentDate,
    status: user?.paymentStatus,
    markedPaidName: user?.markedPaidRecorderName,
    userName: user?.name,
    payerName: user?.payerName,
    payerUserID: user?.payerUserID,
    parentComponent: "Respondent",
    isHovering: isHovering || hasActiveHoverItem,
    isSuggestedDonation: isSuggestedDonation,
    totalAmountPaid: totalAmountPaid,
    signupItemID,
  };

  return (
    <Wrapper highlight={highlight}>
      <TitleRow {...hoverProps}>
        <Name>
          <NameWithProfileLink name={name} userID={userID} />
          {renderNumberOfItemSlots()}
        </Name>

        {hasSignupCost && isSuggestedDonation ? (
          <DonationPill {...pillProps} />
        ) : hasSignupCost ? (
          <PaymentPill {...pillProps} />
        ) : null}

        <TitleEnd>
          <UserSignupHistoryButton
            userSignupHistory={userSignupHistory}
            isHovering={isHovering || hasActiveHoverItem}
            showHistoryPopover={showHistoryPopover}
            setShowHistoryPopover={setShowHistoryPopover}
            signupItemID={signupItemID}
            user={user}
          />

          {isItemType ? (
            <EditNumberOfSlotsButton
              isHovering={isHovering || hasActiveHoverItem}
              showNumSlotsPopover={showNumSlotsPopover}
              setShowNumSlotsPopover={setShowNumSlotsPopover}
              signupItemID={signupItemID}
              user={user}
              paidSlotsCount={paidSlotsCount()}
              setShowDeletePaidSignupModal={setShowDeletePaidSignupModal}
              setUpdatedNumberOfSlots={setUpdatedNumberOfSlots}
              setRemovalType={setRemovalType}
            />
          ) : null}

          <span ref={confirmationRef}>
            <HoverButton
              icon={faTrashAlt}
              isHovering={isHovering || hasActiveHoverItem}
              isActive={showDeletePopover}
              onClick={handleTrashCanClick}
            />
          </span>

          {isMemberType ? (
            <AddRespondent
              isHovering={isHovering || hasActiveHoverItem}
              showAddPopover={showAddPopover}
              setShowAddPopover={setShowAddPopover}
              userID={user.userID}
              signupItemID={signupItemID}
            />
          ) : null}
        </TitleEnd>
        {!isItemType ? <NumberOfSlots>{numberOfSlots}</NumberOfSlots> : null}
      </TitleRow>

      {!isItemType &&
        additionalNames?.map((user) => {
          return (
            <AdditionalName
              key={user.additionalNameID}
              user={user}
              hasSignupCost={hasSignupCost}
              setRemovalType={setRemovalType}
              removalType={removalType}
              signupItemID={signupItemID}
            />
          );
        })}

      {isMemberType ? (
        <ExtraSlotsWithoutNames users={[user]} signupItemID={signupItemID} />
      ) : null}

      <FnrConfirmationPopover
        show={showDeletePopover}
        ref={confirmationRef}
        onClose={() => setShowDeletePopover(false)}
        message={`Are you sure you want to remove ${user.name} from the Signup?`}
        button1={
          <FnrButton
            text="Yes please!"
            onClick={handleDeleteClick}
            color="red"
            loading={status === "loading"}
          />
        }
        color="red"
        style={{ maxWidth: 200, padding: 10 }}
      />

      <DeletePaidSignupModal
        user={user}
        signupItemID={signupItemID}
        showDeletePaidSignupModal={showDeletePaidSignupModal}
        setShowDeletePaidSignupModal={setShowDeletePaidSignupModal}
        removalType={removalType}
        setRemovalType={setRemovalType}
        updatedNumberOfSlots={updatedNumberOfSlots}
      />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  margin-bottom: 8px;
  background-color: ${({ highlight }) => (highlight ? "#eceff0" : null)};
  padding: 4px 8px;
  border-radius: 8px;
`;

const TitleRow = styled(motion.div)`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  font-size: 14px;
`;

const Name = styled.div`
  color: #333;
  font-weight: 400;
  margin-right: 3px;
`;

const TitleEnd = styled.div`
  display: flex;
  align-items: center;
  margin-left: 8px;
`;

const NumberOfSlots = styled.div`
  margin-left: auto;
  color: #333;
  font-weight: 400;
`;
